import { useState } from 'react';

import {
  Badge,
  cardStyle,
  IdChip,
  isBlocked,
  rankIssuesBySeverity,
  realBlockers,
  Section,
  splitCatalogEntry,
  text,
} from './optimizationBreakdownPrimitives';

/**
 * Crimtan DV360 optimization breakdown.
 *
 * The Crimtan optimizer prompt (campaign_optimizer/dv360_optimization_crimtan.txt) asks
 * the model for a diagnosis-first result: work out WHY a line item is not delivering
 * before recommending anything, then pace to the IO goal, then expand. The generic
 * optimization view only rendered the middle third of that — `pacing_recommendations`
 * and `recommendations` became suggestion cards, while `campaign_analysis.key_issues`,
 * `delivery_diagnosis` and `audience_expansion_recommendations` had no renderer at all
 * and were visible only by expanding the raw result JSON.
 *
 * These two sections restore the prompt's own shape around the existing cards:
 *
 *   1. CAUSE           <CrimtanCauseSection>        — why it is not delivering
 *   2. RECOMMENDATION  (the existing suggestion cards, with their Apply buttons)
 *   3. EXPANSIONS      <CrimtanExpansionsSection>   — audiences to add
 *
 * The cards in the middle are deliberately left alone: they own the apply/applied state
 * keyed on the backend's original suggestion index, so wrapping rather than replacing
 * them keeps Apply working exactly as it does on every other platform.
 */

export const CRIMTAN_CLIENT = 'crimtan';

/**
 * True when an optimizer result was steered by the Crimtan client prompt.
 *
 * Keys off `client_name`, which the launcher echoes onto the result with the slug that
 * actually resolved (see DV360CampaignOptimizerLauncher.optimize_campaign). The campaign
 * detail screen never receives the account name, so the result itself is the only place
 * this is knowable — and a slug that resolved is also proof the client prompt really ran,
 * which an account-name check on its own would not give us.
 */
export function isCrimtanOptimizerResult(jsonResponse) {
  const client = jsonResponse?.client_name;
  return typeof client === 'string' && client.toLowerCase() === CRIMTAN_CLIENT;
}

/**
 * Render-safety helpers and the visual vocabulary are shared with the other clients'
 * breakdowns — see optimizationBreakdownPrimitives.js for why they live there. Re-exported
 * because CampaignActivity and this component's tests have always imported several of them
 * from here.
 */
export {
  Badge,
  cardStyle,
  IdChip,
  Section,
  text,
} from './optimizationBreakdownPrimitives';

/* ------------------------------------------------------------------ */
/* 1. CAUSE                                                            */
/* ------------------------------------------------------------------ */

/**
 * Why the campaign is not delivering: the pacing headline, the ranked issues, and the
 * per-line-item delivery diagnosis.
 *
 * Blocked line items are listed in full and unconditionally — a line item that cannot
 * serve is the one thing on this screen that makes every recommendation below it moot,
 * so it must not be behind a disclosure. The line items that CAN deliver are collapsed
 * by default: on a real campaign they are most of the list and carry no action.
 */
export function CrimtanCauseSection({ details }) {
  const [showHealthy, setShowHealthy] = useState(false);

  const ca = (details?.campaign_analysis && typeof details.campaign_analysis === 'object')
    ? details.campaign_analysis
    : {};
  const keyIssues = Array.isArray(ca.key_issues) ? ca.key_issues : [];
  const diagnosis = (Array.isArray(details?.delivery_diagnosis) ? details.delivery_diagnosis : [])
    .filter(Boolean);

  const pacingSummary = text(ca.pacing_summary);
  const overallPerformance = text(ca.overall_performance);

  const blocked = diagnosis.filter(isBlocked);
  const healthy = diagnosis.filter((d) => !isBlocked(d));

  if (!pacingSummary && !overallPerformance && !keyIssues.length && !diagnosis.length) {
    return null;
  }

  const rankedIssues = rankIssuesBySeverity(keyIssues);

  const renderDiagnosisRow = (d, i) => {
    const blockers = realBlockers(d.blockers);
    const lineItemId = text(d.line_item_id);
    const evidence = text(d.evidence);
    const blockedRow = isBlocked(d);
    return (
      // Index is part of the key because two diagnosis entries can name the same line item.
      <div key={`${lineItemId || 'li'}-${i}`} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <strong style={{ fontSize: '0.9em' }}>{text(d.line_item_name) || lineItemId}</strong>
          {lineItemId && <IdChip>{lineItemId}</IdChip>}
          <Badge level={blockedRow ? 'blocked' : 'ok'}>
            {blockedRow ? 'cannot deliver' : 'can deliver'}
          </Badge>
        </div>
        {blockers.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {blockers.map((b, bi) => (
              <Badge key={bi} level="high">{b.replace(/_/g, ' ')}</Badge>
            ))}
          </div>
        )}
        {evidence && (
          <p style={{ margin: 0, fontSize: '0.85em', color: '#444' }}>{evidence}</p>
        )}
      </div>
    );
  };

  return (
    <Section
      step="1"
      title="Cause"
      subtitle="What is holding delivery back, before any change is recommended."
    >
      {pacingSummary && (
        <div style={{
          padding: '12px 15px', background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: '6px', marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
        }}>
          <strong style={{ display: 'block', marginBottom: '4px' }}>Pacing</strong>
          {pacingSummary}
        </div>
      )}

      {overallPerformance && (
        <div style={{
          padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px',
          marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
        }}>
          {overallPerformance}
        </div>
      )}

      {rankedIssues.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Key issues</h6>
          {rankedIssues.map((issue, i) => {
            // An entry may be a bare string rather than an object.
            const issueText = text(typeof issue === 'object' && issue !== null ? issue.issue : issue);
            const severity = text(issue?.severity);
            const affected = (Array.isArray(issue?.affected_line_items) ? issue.affected_line_items : [])
              .map(text).filter(Boolean);
            return (
              <div key={i} style={cardStyle}>
                {severity && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <Badge level={severity}>{severity}</Badge>
                  </div>
                )}
                {issueText && <p style={{ margin: 0, fontSize: '0.88em' }}>{issueText}</p>}
                {affected.length > 0 && (
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {affected.map((li, li_i) => <IdChip key={`${li}-${li_i}`}>{li}</IdChip>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {blocked.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#b91c1c' }}>
            Cannot deliver ({blocked.length})
          </h6>
          {blocked.map(renderDiagnosisRow)}
        </div>
      )}

      {healthy.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <button
            type="button"
            aria-expanded={showHealthy}
            onClick={() => setShowHealthy((v) => !v)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: '#0F7B5F', fontSize: '0.85em', fontWeight: 600,
            }}
          >
            {showHealthy ? '▾' : '▸'} {healthy.length} line item{healthy.length === 1 ? '' : 's'} can deliver
          </button>
          {showHealthy && healthy.map(renderDiagnosisRow)}
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 3. EXPANSIONS                                                       */
/* ------------------------------------------------------------------ */

/**
 * Audiences to add, grouped by the line item they apply to.
 *
 * `audience_expansion_recommendations` is a flat list with one entry per audience, so a
 * line item getting eight audiences arrives as eight rows naming the same line item.
 * Grouping is what makes it readable as "this line item, these audiences".
 *
 * Keyword / domain / PMP-deal expansions are NOT repeated here: they arrive inside
 * `recommendations` and are already rendered as suggestion cards in section 2, with the
 * concrete values in their implementation steps. Duplicating them would make the same
 * change look like two separate asks.
 */
export function CrimtanExpansionsSection({ details }) {
  const raw = Array.isArray(details?.audience_expansion_recommendations)
    ? details.audience_expansion_recommendations
    : [];
  if (!raw.length) return null;

  // Preserve first-seen line-item order rather than sorting: the optimizer emits them in
  // the order it worked through the line items.
  const collected = [];
  const byLineItem = new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const lineItemId = text(entry.line_item_id);
    const key = lineItemId || '—';
    if (!byLineItem.has(key)) {
      const group = { lineItemId, google: [], firstParty: [] };
      byLineItem.set(key, group);
      collected.push(group);
    }
    const group = byLineItem.get(key);
    const google = splitCatalogEntry(entry.google_audience_to_expand);
    if (google) group.google.push(google);
    const firstParty = splitCatalogEntry(entry.first_party_audience_to_expand);
    if (firstParty) group.firstParty.push(firstParty);
  }

  // A group whose every audience value was null or unrenderable would otherwise render an
  // empty card, and its line item would still be counted in the "N audiences across M line
  // items" subtitle — a header promising expansions above a card containing none.
  const groups = collected.filter((g) => g.google.length || g.firstParty.length);
  if (!groups.length) return null;

  const audienceList = (label, items) => {
    if (!items.length) return null;
    return (
      <div style={{ marginTop: '6px' }}>
        <span style={{ fontSize: '0.78em', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 700 }}>
          {label}
        </span>
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '0.85em', color: '#374151' }}>
          {items.map((a, i) => (
            <li key={`${a.id || a.name}-${i}`} style={{ marginBottom: '2px' }}>
              {a.name}
              {a.id && <> {' '}<IdChip>{a.id}</IdChip></>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const totalAudiences = groups.reduce((n, g) => n + g.google.length + g.firstParty.length, 0);

  return (
    <Section
      step="3"
      title="Expansions"
      subtitle={`${totalAudiences} audience${totalAudiences === 1 ? '' : 's'} to add across ${groups.length} line item${groups.length === 1 ? '' : 's'}. Add these in DV360 under the line item's audience targeting — quote the id, not the name.`}
    >
      {groups.map((g, i) => (
        <div key={g.lineItemId || i} style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.9em' }}>Line item</strong>
            {g.lineItemId ? <IdChip>{g.lineItemId}</IdChip> : <span style={{ fontSize: '0.85em', color: '#6b7280' }}>unspecified</span>}
          </div>
          {audienceList('Google audiences', g.google)}
          {audienceList('First-party audiences', g.firstParty)}
        </div>
      ))}
    </Section>
  );
}
